import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaCamera, FaImage, FaSync, FaExclamationTriangle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useToasts } from '../hooks/ToastContext';
import '../styles/QRScannerModal.css';

const QRScannerModal = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const { addToast } = useToasts();
    const scannerRef = useRef(null);
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const fileInputRef = useRef(null);
    const scannerId = "qr-reader";

    // 1. Robust Lifecycle: Stop scanner properly
    const stopScanner = useCallback(async () => {
        if (scannerRef.current && scannerRef.current.isScanning) {
            try {
                await scannerRef.current.stop();
            } catch (err) {
                console.warn("Non-critical scanner stop error:", err);
            }
        }
        setIsCameraActive(false);
    }, []);

    const handleDecodedText = useCallback((text) => {
        // Handle various URL patterns including localhost and production domains
        const fertilizerMatch = text.match(/\/fertilizer\/([^/?#]+)/);

        if (fertilizerMatch && fertilizerMatch[1]) {
            const slug = fertilizerMatch[1];
            stopScanner();
            onClose();
            addToast("Fertilizer QR Detected!", "success");
            navigate(`/fertilizer/${slug}`);
        } else {
            // Attempt to treat the whole text as a slug if it doesn't look like a URL
            if (!text.startsWith('http')) {
                const slug = text.trim();
                stopScanner();
                onClose();
                addToast("Fertilizer Code Detected!", "success");
                navigate(`/fertilizer/${slug}`);
            } else {
                addToast("Invalid QR: Use an OBFS Fertilizer Code", "error");
            }
        }
    }, [stopScanner, onClose, addToast, navigate]);

    // 2. Multi-Pass Scanning Strategy
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !scannerRef.current) return;

        setIsProcessing(true);
        
        // CRITICAL: Stop camera before file scanning to avoid engine lock
        await stopScanner();

        try {
            // PASS 1: Standard scan (works for high-quality photos)
            const decodedText = await scannerRef.current.scanFile(file, true);
            handleDecodedText(decodedText);
        } catch (err) {
            console.warn("Standard scan failed, attempting pre-processing...");
            
            try {
                // PASS 2: Add white padding (Quiet Zone) + Normalization
                const result = await scanWithPreprocessing(file);
                if (result) {
                    handleDecodedText(result);
                } else {
                    throw new Error("Detection failed");
                }
            } catch (finalErr) {
                addToast("Could not detect QR. Use a clearer photo or scan directly.", "error");
                startCamera(); // Restart camera if upload fails
            }
        } finally {
            setIsProcessing(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    // 3. Image Pre-processor (Fixes "naked" QRs and high-res noise)
    const scanWithPreprocessing = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = async () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    
                    // Normalize size to 800px max (prevents engine choking on massive photos)
                    const MAX_DIM = 800;
                    const scale = Math.min(MAX_DIM / img.width, MAX_DIM / img.height, 1);
                    const w = img.width * scale;
                    const h = img.height * scale;
                    
                    // Add 60px white padding (The "Quiet Zone" fix for digital QRs)
                    const pad = 60;
                    canvas.width = w + (pad * 2);
                    canvas.height = h + (pad * 2);
                    
                    ctx.fillStyle = "#ffffff";
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, pad, pad, w, h);

                    // Optional: Basic Thresholding/Grayscale for contrast boost
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const data = imageData.data;
                    for (let i = 0; i < data.length; i += 4) {
                        const avg = (data[i] + data[i+1] + data[i+2]) / 3;
                        data[i] = data[i+1] = data[i+2] = avg; 
                    }
                    ctx.putImageData(imageData, 0, 0);

                    canvas.toBlob(async (blob) => {
                        try {
                            const processedFile = new File([blob], "qr.png", { type: "image/png" });
                            const text = await scannerRef.current.scanFile(processedFile, false);
                            resolve(text);
                        } catch {
                            resolve(null);
                        }
                    }, 'image/png');
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    };

    const startCamera = useCallback(async () => {
        if (!scannerRef.current) return;
        try {
            setError(null);
            await scannerRef.current.start(
                { facingMode: "environment" },
                { 
                    fps: 15, 
                    qrbox: { width: 250, height: 250 },
                    aspectRatio: 1.0
                },
                (text) => handleDecodedText(text),
                () => {} // Silent failures for camera frames
            );
            setIsCameraActive(true);
        } catch (err) {
            console.error("Camera access error:", err);
            setError("Camera access denied or unavailable.");
            setIsCameraActive(false);
        }
    }, [handleDecodedText]);

    useEffect(() => {
        if (isOpen) {
            // Initialize scanner with native hardware support enabled
            scannerRef.current = new Html5Qrcode(scannerId, {
                formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
                experimentalFeatures: { useBarCodeDetectorIfSupported: true }
            });
            startCamera();
        } else {
            stopScanner();
        }
        return () => {
            if (scannerRef.current) {
                stopScanner();
            }
        };
    }, [isOpen, startCamera, stopScanner]);

    if (!isOpen) return null;

    return (
        <div className="qr-scanner-overlay" onClick={onClose}>
            <AnimatePresence>
                <motion.div 
                    className="qr-scanner-content" 
                    initial={{ y: "100%" }} 
                    animate={{ y: 0 }} 
                    exit={{ y: "100%" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="scanner-header">
                        <div className="header-drag-handle"></div>
                        <div className="header-main">
                            <h3>Scan Fertilizer QR</h3>
                            <button className="close-btn" onClick={onClose}><FaTimes /></button>
                        </div>
                    </div>

                    <div className="scanner-body">
                        <div className="scanner-viewport-container">
                            <div id={scannerId}></div>
                            {isProcessing && (
                                <div className="scanner-status-overlay">
                                    <div className="spinner"></div>
                                    <p>Processing Image...</p>
                                </div>
                            )}
                            {error && (
                                <div className="scanner-error-overlay">
                                    <FaExclamationTriangle size={40} />
                                    <p>{error}</p>
                                    <button className="btn btn-secondary btn-sm mt-md" onClick={startCamera}>
                                        <FaSync /> Retry Camera
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="scanner-instructions">
                            <p>Point at a fertilizer bag QR or upload a photo from your gallery</p>
                        </div>

                        <div className="scanner-actions">
                            <input 
                                type="file" 
                                accept="image/*" 
                                style={{ display: 'none' }} 
                                ref={fileInputRef} 
                                onChange={handleFileUpload} 
                            />
                            <button 
                                className="action-btn upload-btn" 
                                onClick={() => fileInputRef.current.click()} 
                                disabled={isProcessing}
                            >
                                <FaImage />
                                <span>Gallery</span>
                            </button>
                            
                            <button 
                                className="action-btn camera-btn" 
                                onClick={startCamera} 
                                disabled={isCameraActive || isProcessing}
                            >
                                <FaCamera />
                                <span>Camera</span>
                            </button>
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default QRScannerModal;
